import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { KaryawanAdapter } from '../../adapter/pegawai/karyawan.adapter.service';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Karyawan } from '../../../models/pegawai/karyawan.model';
import { ApiService } from '../api.service';
import { ClinicEnvironmentService } from '../../clinic-environment.service';
import { RiwayatPendidikan } from '../../../models/pegawai/riwayat-pendidikan.model';
import { RiwayatJabatan } from '../../../models/pegawai/riwayat-jabatan.model';
import { RiwayatKepangkatanAdapter } from '../../adapter/pegawai/riwayat-kepangkatan.adapter.service';
import { RiwayatKepangkatan } from '../../../models/pegawai/riwayat-kepangkatan.model';
import { RiwayatJabatanAdapter } from '../../adapter/pegawai/riwayat-jabatan.adapter.service';
import { RiwayatPendidikanAdapter } from '../../adapter/pegawai/riwayat-pendidikan.adapter.service';

@Injectable({
  providedIn: 'root',
})
export class KaryawanApiService implements ApiService<Karyawan> {
  private nextPage: string;

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(
    private http: HttpClient,
    private adapter: KaryawanAdapter,
    private riwayatKepangkatanAdapter: RiwayatKepangkatanAdapter,
    private riwayatPendidikanAdapter: RiwayatPendidikanAdapter,
    private riwayatJabatanAdapter: RiwayatJabatanAdapter,
    private clinicEnv: ClinicEnvironmentService
  ) {}

  public getAll(query?: any): Observable<Karyawan[]> {
    return this.http
      .get(`${environment.urlV2}/data-klinik/${this.clinicEnv.clinicId}/karyawan/all${query || ''}`)
      .pipe(
        map((data: any) => {
          if (!data) {
            return [];
          }
          return data.responses.karyawan.map((item: Karyawan) => this.adapter.adapt(item));
        }),
        catchError((err) => throwError(err))
      );
  }

  public search(text: string): Observable<Karyawan[]> {
    return this.http
      .get(
        `${environment.urlV2}/data-klinik/${this.clinicEnv.clinicId}/karyawan/search?name=${text}`
      )
      .pipe(
        map((data: any) => {
          if (!data) {
            return [];
          }
          return data.responses.karyawan.map((item: Karyawan) => this.adapter.adaptV2(item));
        }),
        catchError((err) => throwError(err))
      );
  }

  public getDokter(): Observable<Karyawan[]> {
    return this.http
      .get(`${environment.urlV2}/data-klinik/${this.clinicEnv.clinicId}/karyawan/dokter`)
      .pipe(
        map((data: any) => {
          if (!data) {
            return [];
          }
          return data.responses.karyawan.map((item: Karyawan) => this.adapter.adapt(item));
        }),
        catchError((err) => throwError(err))
      );
  }

  public getKasir(): Observable<Karyawan[]> {
    return this.http
      .get(`${environment.urlV2}/data-klinik/${this.clinicEnv.clinicId}/get-nama-kasir`)
      .pipe(
        map((data: any) => {
          if (!data) {
            return [];
          }
          return data.responses.kasir.map((item: Karyawan) => this.adapter.adapt(item));
        }),
        catchError((err) => throwError(err))
      );
  }

  public getKaryawanSelection(id: number): Observable<Karyawan[]> {
    return this.http
      .get(`${environment.urlV2}/data-klinik/${this.clinicEnv.clinicId}/karyawan/${id}/selection`)
      .pipe(
        map((data: any) => {
          if (!data) {
            return [];
          }
          return data.responses.kasir.map((item: Karyawan) => this.adapter.adapt(item));
        }),
        catchError((err) => throwError(err))
      );
  }

  public getById(id: number): Observable<Karyawan> {
    let queries = '';
    if (this.clinicEnv.clinicId) {
      queries = `?dataKlinikId=${this.clinicEnv.clinicId}`;
    }
    return this.http.get(`${environment.urlV1}/karyawan/${id}${queries}`).pipe(
      map((data: any) => {
        if (!data) {
          return null;
        }
        return this.adapter.adapt(data);
      }),
      catchError((err) => throwError(err))
    );
  }

  public getByKodeDokterBpjs(kodeDokterBpjs: string): Observable<Karyawan> {
    return this.http
      .get(
        `${environment.urlV2}/data-klinik/${this.clinicEnv.clinicId}/kode-dokter-bpjs/${kodeDokterBpjs}/karyawan`
      )
      .pipe(
        map((data: any) => {
          if (!data) {
            return null;
          }
          return this.adapter.adaptV2(data.responses.karyawan[0]);
        }),
        catchError((err) => throwError(err))
      );
  }

  public getStaffingHistories(id: number): Observable<any> {
    return this.http.get(`${environment.urlV2}/karyawan/${id}/staffing-histories`).pipe(
      map((data: any) => {
        if (!data) {
          return null;
        }
        const kepangkatan = data.responses.karyawan.kepangkatan.map((item: RiwayatKepangkatan) =>
          this.riwayatKepangkatanAdapter.adapt(item)
        );
        const pendidikan = data.responses.karyawan.pendidikan.map((item: RiwayatPendidikan) =>
          this.riwayatPendidikanAdapter.adapt(item)
        );
        const jabatan = data.responses.karyawan.jabatan.map((item: RiwayatJabatan) =>
          this.riwayatJabatanAdapter.adapt(item)
        );
        return {
          kepangkatan,
          pendidikan,
          jabatan,
        };
      }),
      catchError((err) => throwError(err))
    );
  }

  public getAkses(id: number): Observable<Karyawan[]> {
    return this.http
      .get(`${environment.urlV2}/data-klinik/${this.clinicEnv.clinicId}/karyawan/${id}/hak-akses`)
      .pipe(
        map((data: any) => {
          if (!data) {
            return [];
          }
          return data.responses.referenceNavigasi.map((item: Karyawan) =>
            this.adapter.adaptV2(item)
          );
        }),
        catchError((err) => throwError(err))
      );
  }

  public create(data: any, signature?: string): Observable<any> {
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    if (signature) {
      httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Access-Key': signature,
        }),
      };
    }
    return this.http
      .post(`${environment.urlV2}/karyawan`, data, httpOptions)
      .pipe(catchError((err) => throwError(err)));
  }

  public update(id: number, data: any): Observable<any> {
    return this.http
      .patch(`${environment.urlV2}/karyawan/${id}`, data, this.httpOptions)
      .pipe(catchError((err) => throwError(err)));
  }

  public delete(id: number): Observable<any> {
    return this.http
      .patch(`${environment.urlV2}/karyawan/${id}/hide`, {})
      .pipe(catchError((err) => throwError(err)));
  }

  public deactivate(id: number, data: any): Observable<any> {
    return this.http
      .patch(`${environment.urlV2}/karyawan/${id}/deactivate`, data)
      .pipe(catchError((err) => throwError(err)));
  }
}
